import React, { useCallback, useEffect, useMemo } from 'react';
import EditableTable, {
  IEditableTableColumns,
} from '@components/EditableTable';
import { Form, FormInstance, Select, Tag } from 'antd';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import ActionTypes from '@utils/deviceActions';
import useSwr from '@hooks/useSwr';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';
import { useTranslation } from 'react-i18next';
import { IDevice } from '@pages/Assets/Inventory/types';

interface MotOperatorChangeDeviceProps {
  msisdn: string;
  vendor: string | null;
  operator?: string;
  processData?: string | number | null;
}

interface MotOperatorChangePayloadProps {
  action: ActionTypes.MOT_OPERATOR_CHANGE;
  devices: MotOperatorChangeDeviceProps[];
}

interface MotOperatorChangeProps {
  devices: IDevice[];
  isUpload?: boolean;
  form: FormInstance<any>;
  actionRole: RolesTypes | undefined;
}

const useMotOperatorChange = ({
  devices,
  isUpload,
  form,
  actionRole,
}: MotOperatorChangeProps): DeviceActionProps => {
  const { t } = useTranslation(['actions-modal', 'attributes']);
  const changedDevices = Form.useWatch('changedDevices', form);
  const newItem = Form.useWatch('newItem', form);

  const { data: operatorsData } = useSwr<
    { idOperator: number; name: string }[]
  >(
    actionRole === rolesConstants.MOT_OPERATOR_CHANGE
      ? '/service-proxy/mot-operators'
      : null,
  );

  const operatorsOptions = useMemo(
    () =>
      operatorsData?.map(({ name }) => ({
        label: name,
        value: name,
      })),
    [operatorsData],
  );

  const treatedDevices = useMemo(
    (): MotOperatorChangeDeviceProps[] =>
      devices?.map(device => ({
        msisdn: device.msisdn,
        vendor: device.vendor,
        operator: device.operator,
      })),
    [devices],
  );

  const isOnlyOneDevice = useMemo(
    () => treatedDevices?.length === 1,
    [treatedDevices],
  );

  const handleTableChange = useCallback(
    (items: { [key: string]: any }[]) => {
      form?.setFieldValue('changedDevices', items);
    },
    [form],
  );

  useEffect(() => {
    if (actionRole === rolesConstants.MOT_OPERATOR_CHANGE && !changedDevices) {
      handleTableChange(treatedDevices);
    }
  }, [actionRole, changedDevices, handleTableChange, treatedDevices]);

  const columns = useMemo(
    (): IEditableTableColumns[] => [
      {
        title: t('attributes:msisdn'),
        key: 'msisdn',
        dataIndex: 'msisdn',
      },
      {
        title: t('attributes:vendor'),
        key: 'operator',
        dataIndex: 'operator',
      },
      {
        title: t('attributes:newVendor'),
        key: 'processData',
        dataIndex: 'processData',
        itemInputType: 'select',
        editable: true,
        selectProps: {
          options: operatorsOptions,
          showSearch: true,
          optionLabelProp: 'label',
        },
      },
    ],
    [t, operatorsOptions],
  );

  const steps: DeviceActionStepsProps[] = useMemo(
    () => [
      {
        title: t('attributes:newVendor'),
        name: isOnlyOneDevice ? 'newItem' : 'changedDevices',
        itemInfo: isOnlyOneDevice && (
          <p
            style={{
              fontSize: '0.8rem',
              marginTop: -8,
              marginBottom: 4,
              textAlign: 'right',
            }}
          >
            {`${t('mot-operator-change.current')}: `}
            <strong style={{ fontWeight: 'bold' }}>
              {devices?.[0]?.operator}
            </strong>
          </p>
        ),
        content: isOnlyOneDevice ? (
          <Select
            allowClear
            options={operatorsOptions}
            style={{ width: '100%' }}
          />
        ) : (
          <EditableTable
            data={changedDevices}
            columns={columns}
            onChange={handleTableChange}
          />
        ),
      },
    ],
    [
      t,
      isOnlyOneDevice,
      devices,
      operatorsOptions,
      changedDevices,
      columns,
      handleTableChange,
    ],
  );

  const payloadToSend = useMemo((): MotOperatorChangePayloadProps => {
    const payload: MotOperatorChangePayloadProps = {
      action: ActionTypes.MOT_OPERATOR_CHANGE,
      devices: treatedDevices?.map(device => ({
        msisdn: device.msisdn,
        vendor: device.vendor,
      })),
    };

    if (!isUpload) {
      const devicesData: MotOperatorChangeDeviceProps[] = isOnlyOneDevice
        ? treatedDevices
        : changedDevices;

      payload.devices = devicesData?.map(device => ({
        msisdn: device.msisdn,
        vendor: isOnlyOneDevice ? newItem : device.processData,
      }));
    }

    return payload;
  }, [changedDevices, newItem, isOnlyOneDevice, isUpload, treatedDevices]);

  const uploadCustomInfo = useMemo(
    () =>
      operatorsData && (
        <p style={{ paddingLeft: 6 }}>
          {`${t('mot-operator-change.available-operators')}: `}
          {operatorsData.map(operator => (
            <Tag key={operator.idOperator}>{operator.name}</Tag>
          ))}
        </p>
      ),
    [operatorsData, t],
  );

  return {
    steps,
    modalWidth: isOnlyOneDevice ? 380 : 580,
    payloadToSend,
    uploadCustomInfo,
  };
};

export default useMotOperatorChange;
